import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import { appendNeedToCallItemForOwner } from '../services/noteService';

const userDisplayName = (user) => {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.username || 'Someone';
};

const userPhone = (user) =>
  String(user?.mobileNumber || user?.contactNumber || '').trim();

const initialsFromName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
};

const CallThisPersonModal = ({
  isOpen,
  onClose,
  onSaved,
  initialUser = null,
  ownerUserId = null,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const users = useSelector((state) => state.users.users || []);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [pickedUserId, setPickedUserId] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchUsers());
    setName(initialUser ? userDisplayName(initialUser) : '');
    setPhone(initialUser ? userPhone(initialUser) : '');
    setReason('');
    setPickedUserId(initialUser?.id || '');
    setSuggestionsOpen(false);
    setSaving(false);
    setError('');
  }, [dispatch, initialUser, isOpen]);

  const query = name.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (query.length < 1) return [];
    return users
      .filter((user) => user.id !== currentUser?.id)
      .filter((user) => {
        const label = userDisplayName(user).toLowerCase();
        const username = String(user.username || '').toLowerCase();
        return label.includes(query) || username.includes(query);
      })
      .slice(0, 8);
  }, [users, currentUser?.id, query]);

  if (!isOpen || typeof document === 'undefined') return null;

  const pickUser = (user) => {
    setPickedUserId(user.id);
    setName(userDisplayName(user));
    setPhone(userPhone(user));
    setSuggestionsOpen(false);
    setError('');
  };

  const submit = async () => {
    if (!ownerUserId) {
      setError('Open a 1-to-1 chat to add this to the other person');
      return;
    }
    const nextName = name.trim();
    const nextPhone = phone.trim();
    if (!nextName) {
      setError('Whom is required');
      return;
    }
    if (!nextPhone) {
      setError('Phone number is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await appendNeedToCallItemForOwner(ownerUserId, {
        targetUserId: pickedUserId || null,
        name: nextName,
        phone: nextPhone,
        reason,
      });
      onSaved?.({ name: nextName, phone: nextPhone, reason: reason.trim() });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not add to Need to call');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="call-this-person-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 id="call-this-person-title" className="text-base font-semibold text-gray-900 dark:text-white pr-8">
          Call this person
        </h3>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Search a Workloop teammate or type a name. This is saved on their Need to call list. Phone number is required.
        </p>
        <div className="relative mt-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
            Whom
            <input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setPickedUserId('');
                setSuggestionsOpen(true);
                setError('');
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => window.setTimeout(() => setSuggestionsOpen(false), 120)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submit();
                }
                if (event.key === 'Escape') setSuggestionsOpen(false);
              }}
              placeholder="Name or teammate"
              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              autoFocus
            />
          </label>
          {suggestionsOpen && suggestions.length > 0 ? (
            <ul className="absolute left-0 right-0 z-20 mt-1 max-h-44 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
              {suggestions.map((user) => {
                const label = userDisplayName(user);
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pickUser(user)}
                      className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-sky-50 dark:hover:bg-sky-950/40"
                    >
                      <PresignedAvatar
                        fileKey={user.profilePicture}
                        cacheKey={user.username}
                        name={label}
                        initials={initialsFromName(label)}
                        className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                        fallbackClassName="flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white text-[10px] font-bold flex-shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium text-gray-900 dark:text-white">
                          {label}
                        </span>
                        {user.username ? (
                          <span className="block truncate text-[10px] text-gray-400">@{user.username}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Phone number
          <input
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Required"
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Reason
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Optional"
            className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        {error ? (
          <p className="mt-3 text-[11px] text-rose-600 dark:text-rose-400">{error}</p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !name.trim() || !phone.trim()}
            onClick={submit}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add to their Need to call'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CallThisPersonModal;
